import React, { type ComponentProps, forwardRef } from 'react';
import type { Text as RNText } from 'react-native';
import { StyleSheet } from 'react-native';

import { TYPE_CONFIG, WEIGHT_MAP } from '@garnish/constants';

import { useResponsive } from '../../../hooks';
import { Text } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const TitleText = forwardRef<RNText, TitleTextProps>((props, ref) => {
  const { children, sizeMatch = TITLE_TEXT_DEFAULT_SIZE_MATCH, bold, style, ...restProps } = props;

  const { match } = useResponsive();

  const fontSize = match(sizeMatch);
  const textStyles = TITLE_TEXT_STYLES[fontSize] ?? TITLE_TEXT_DEFAULT_FONT_STYLES;
  const boldTextStyles = bold ? styles.boldText : undefined;
  const allStyles = [textStyles, boldTextStyles, style];

  return (
    <Text ref={ref} style={allStyles} bold={bold} {...restProps}>
      {children}
    </Text>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const TITLE_TEXT_STYLES = TYPE_CONFIG.TITLE;

const TITLE_TEXT_DEFAULT_SIZE_MATCH: TitleTextFontSize[] = ['40', '64', '80'];
const TITLE_TEXT_DEFAULT_FONT_SIZE: TitleTextFontSize = '80';

const TITLE_TEXT_DEFAULT_FONT_STYLES = TITLE_TEXT_STYLES[TITLE_TEXT_DEFAULT_FONT_SIZE];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  boldText: {
    fontWeight: WEIGHT_MAP.bold,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TitleTextProps = Readonly<{
  sizeMatch?: TitleTextFontSize[];
}> &
  ComponentProps<typeof Text>;

type TitleTextFontSize = keyof typeof TITLE_TEXT_STYLES;
